/**
 * CollegeLayoutRoute — gates the college workspace (rosters, readiness,
 * shortlisting, imports) behind "this account belongs to an institution".
 *
 * Every page under this layout calls a `tenant()` API route, which resolves the
 * college from the signed-in user and rejects with 400 "Account is not linked
 * to an institution" when there isn't one. A platform admin has no TeamMember
 * row, so opening /students dumped that raw server error into a red box on an
 * otherwise working page — it read like a bug rather than "wrong account for
 * this view", and offered no way forward.
 *
 * Gate on `user.tenantId` rather than on the role: it is the same value
 * resolveTenantId() uses server-side, so the screen a user sees and the answer
 * the API gives can't drift apart. Partner and support logins have no tenant
 * either and land here for the same reason.
 *
 * Deliberately NOT gated: /reports, /companies, /skills, /tests and /plans.
 * Those serve master/global data to a platform admin (the rail relabels them
 * "Master …") and return 200 without a tenant — blocking them would remove
 * working pages.
 */

import { Suspense } from 'react';
import { Link, Navigate, Outlet } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useAuth } from '../../auth';

/** Paths that require a college. Verified against the API: each one 400s with
 *  "Account is not linked to an institution" for a tenant-less account. */
export const COLLEGE_PATHS = [
  '/overview', '/dashboard', '/students', '/shortlist', '/recent', '/imports', '/autopilot',
];

export function isCollegePath(path: string): boolean {
  return COLLEGE_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

/** Shown instead of the page when the signed-in account has no college. */
function NoInstitutionGate({ name }: { name?: string | null }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--fb-bg2, oklch(0.98 0.002 270))' }}>
      <div
        className="w-full max-w-md rounded-2xl p-8 text-center"
        style={{
          background: 'var(--fb-bg, white)',
          border: '1px solid var(--fb-line, oklch(0.92 0.004 270))',
        }}
      >
        <div
          className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-xl"
          style={{ background: 'var(--fb-bg2, oklch(0.96 0.004 270))' }}
        >
          <Building2 size={22} style={{ color: 'var(--fb-ink2, oklch(0.45 0.01 270))' }} aria-hidden />
        </div>

        <h1 className="text-[17px] font-bold mb-2" style={{ color: 'var(--fb-ink, oklch(0.22 0.01 270))' }}>
          This is a college workspace
        </h1>

        <p className="text-[13px] leading-relaxed mb-6" style={{ color: 'var(--fb-ink2, oklch(0.45 0.01 270))' }}>
          {name ? <><b>{name}</b> isn’t</> : 'This account isn’t'} linked to an institution.
          Student rosters, readiness and shortlisting belong to a specific college — open them
          with a <b>TPO</b> or <b>college admin</b> account.
        </p>

        <div className="flex flex-col gap-2">
          <Link
            to="/platform"
            className="w-full rounded-lg px-4 py-2.5 text-[13px] font-semibold no-underline"
            style={{ background: 'oklch(0.545 0.193 264)', color: 'white' }}
          >
            Go to Platform Console
          </Link>
          <Link
            to="/logout"
            className="w-full rounded-lg px-4 py-2.5 text-[13px] font-semibold no-underline"
            style={{
              background: 'transparent',
              color: 'var(--fb-ink2, oklch(0.45 0.01 270))',
              border: '1px solid var(--fb-line, oklch(0.92 0.004 270))',
            }}
          >
            Sign in as a college account
          </Link>
        </div>
      </div>
    </div>
  );
}

export function CollegeLayoutRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) return <Loading />;
  if (!user) return <Navigate to="/" replace />;

  const tenantId = (user as { tenantId?: string | null }).tenantId;
  if (!tenantId) {
    return <NoInstitutionGate name={(user as { name?: string | null }).name} />;
  }

  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
}
